import { datadogRum } from '@datadog/browser-rum';
import { AnswerStateSchema } from '@repo/contracts/cardfinder/funnel/schema';
import {
  CreatedSessionSchema,
  FUNNEL_CONFIG_VERSION_MISMATCH,
  INVALID_SESSION_TOKEN,
  SessionErrorBodySchema,
} from '@repo/contracts/cardfinder/funnel/session';
import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import * as v from 'valibot';
import type { CardfinderSessionStatus } from '@/shared/api/db/tables';
import type { SessionSaveOutcome, SessionWriteResult } from './sessionWriteResult';

const SESSION_PERSISTENCE_API = '/api/credit-cards/cardfinder/session';

const FETCH_LATEST_SESSION_TIMEOUT_MS = 8_000;

async function parseBody<TSchema extends v.GenericSchema>(
  res: Response,
  schema: TSchema,
): Promise<v.InferOutput<TSchema> | undefined> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return undefined;
  }

  const parsed = v.safeParse(schema, body);
  return parsed.success ? parsed.output : undefined;
}

async function readErrorCode(res: Response): Promise<string | undefined> {
  return (await parseBody(res, SessionErrorBodySchema))?.code;
}

async function classifyFailure(res: Response, context: string): Promise<SessionWriteResult> {
  if (res.status === 409) {
    return (await readErrorCode(res)) === FUNNEL_CONFIG_VERSION_MISMATCH
      ? 'version-mismatch'
      : 'already-complete';
  }
  if (res.status === 401 && (await readErrorCode(res)) === INVALID_SESSION_TOKEN) {
    return 'invalid-token';
  }
  if (res.status === 404) return 'not-found';
  datadogRum.addError(new Error(`${context} failed (${res.status})`));
  return 'error';
}

export async function writeServerSession(
  sessionId: string,
  value: AnswerState,
  options?: { status?: CardfinderSessionStatus; funnelConfigVersion?: string },
): Promise<SessionWriteResult> {
  if (typeof window === 'undefined') return 'ok';
  try {
    const res = await fetch(`${SESSION_PERSISTENCE_API}/${sessionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        answers: value,
        status: options?.status ?? 'incomplete',
        funnelConfigVersion: options?.funnelConfigVersion,
      }),
    });
    if (res.ok) return 'ok';
    return classifyFailure(res, 'Session write');
  } catch (error) {
    datadogRum.addError(error instanceof Error ? error : new Error('Session write failed'));
    return 'error';
  }
}

export async function createServerSession(
  answers: AnswerState,
  status: CardfinderSessionStatus,
  funnelConfigVersion?: string,
): Promise<SessionSaveOutcome> {
  try {
    const res = await fetch(SESSION_PERSISTENCE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers, status, funnelConfigVersion }),
    });
    if (!res.ok) return { result: await classifyFailure(res, 'Session create'), sessionId: null };

    const created = await parseBody(res, CreatedSessionSchema);
    if (!created) {
      datadogRum.addError(new Error('Session create returned an unreadable body'));
      return { result: 'error', sessionId: null };
    }
    return { result: 'ok', sessionId: created.session_id };
  } catch (error) {
    datadogRum.addError(error instanceof Error ? error : new Error('Session create failed'));
    return { result: 'error', sessionId: null };
  }
}

export async function fetchLatestCompleteSession(): Promise<AnswerState | null> {
  if (typeof window === 'undefined') return null;
  try {
    const res = await fetch(`${SESSION_PERSISTENCE_API}?status=complete`, {
      signal: AbortSignal.timeout(FETCH_LATEST_SESSION_TIMEOUT_MS),
    });
    if (res.ok) {
      const answers = await parseBody(res, AnswerStateSchema);
      if (!answers) {
        datadogRum.addError(new Error('Latest session returned an unreadable body'));
        return null;
      }
      return answers;
    }
    if (res.status !== 404) {
      datadogRum.addError(new Error(`Latest session fetch failed (${res.status})`));
    }
    return null;
  } catch (error) {
    datadogRum.addError(error instanceof Error ? error : new Error('Latest session fetch failed'));
    return null;
  }
}
